import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";

import { GrowlContainerComponent } from "@lantern/app/common/utils/growl-components";
import {
  Overlay2Service,
  OverlayAttachment
} from "@lantern/app/core/utils/overlays";

import { GrowlNotice } from "./growl-notice";
import { STANDARD_GROWLS, StandardGrowl } from "./standard-growls";

@Injectable()
export class GrowlService {
  private growlOverlayAttachment: OverlayAttachment<GrowlContainerComponent> | null;

  constructor(private overlayService: Overlay2Service) {}

  open(notice: GrowlNotice | StandardGrowl): void {
    if (this.growlOverlayAttachment == null) {
      this.createGrowlContainer();
    }

    // timeout ensures entry animation runs by allowing container to appear before first notice
    setTimeout(() => {
      this.addNoticeToContainer(
        typeof notice === "string" ? STANDARD_GROWLS[notice] : notice
      );
    });
  }

  success(message: string): void {
    this.open({ type: "success", message });
  }

  warning(message: string): void {
    this.open({ type: "warning", message });
  }

  danger(message: string): void {
    this.open({ type: "danger", message });
  }

  private addNoticeToContainer(notice: GrowlNotice): void {
    if (this.growlOverlayAttachment == null) {
      return;
    }

    this.getGrowlContainerComponent()!.addNotice(notice);
  }

  private createGrowlContainer(): void {
    if (this.growlOverlayAttachment != null) {
      return;
    }

    this.growlOverlayAttachment = this.overlayService.attachComponent(
      GrowlContainerComponent
    );

    this.subToNoticesEmptied(this.getGrowlContainerComponent()!);
  }

  private destroyGrowlContainer(): void {
    if (this.growlOverlayAttachment == null) {
      return;
    }

    this.growlOverlayAttachment.dispose();
    this.growlOverlayAttachment = null;
  }

  private getGrowlContainerComponent(): GrowlContainerComponent | undefined {
    if (this.growlOverlayAttachment == null) {
      return undefined;
    }

    return this.growlOverlayAttachment.componentInstance;
  }

  private growlNoticesEmptiedSubscription: Subscription;
  private subToNoticesEmptied(
    containerComponent: GrowlContainerComponent
  ): void {
    this.unsubFromNoticesEmptied();
    this.growlNoticesEmptiedSubscription =
      containerComponent.noticesEmptied.subscribe(() => {
        this.destroyGrowlContainer();
      });
  }

  private unsubFromNoticesEmptied(): void {
    if (this.growlNoticesEmptiedSubscription != null) {
      this.growlNoticesEmptiedSubscription.unsubscribe();
    }
  }
}
